import { useEffect, useRef, useState } from 'react'
import { Alert, App, Button, Form, Input, Modal, Select, Space, Typography, Upload } from 'antd'
import type { UploadFile } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { errorTag, translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import type { Money } from '@/shared/lib/money'
import { seededName } from '@/shared/lib/seededName'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ENTRY_KINDS,
  REASON_MAX,
  createEntry,
  createTransfer,
  fetchExpenseAccounts,
  uploadAttachment,
  type AttachmentOwner,
  type EntryKind,
} from './cashdeskApi'
import { CashBranchSelect } from './CashBranchSelect'
import {
  TRANSFER_REFERENCE_MAX,
  referenceLabel,
  referenceProblem,
  transferTypeName,
  useTransferTypes,
} from './transferTypeApi'
import { useCashBranches } from './useCashBranches'

export type CashFormMode = 'entry' | 'transfer'

interface CashFormValues {
  branchId?: string
  kind?: EntryKind
  amount: Money | null
  expenseAccountId?: string
  reason?: string
  transferType?: string
  externalReference?: string
  responsiblePerson?: string
  remarks?: string
}

const FIELD_ERRORS: Readonly<Record<string, keyof CashFormValues>> = {
  CASH_TRANSFER_TYPE_REQUIRED: 'transferType',
  CASH_TRANSFER_REFERENCE_REQUIRED: 'externalReference',
  CASH_TRANSFER_REFERENCE_DUPLICATE: 'externalReference',
  CASH_TRANSFER_REFERENCE_TOO_LONG: 'externalReference',
  CASH_TRANSFER_REFERENCE_TOO_SHORT: 'externalReference',
  CASH_TRANSFER_REFERENCE_FORMAT: 'externalReference',
  CASH_TRANSFER_TYPE_UNKNOWN: 'transferType',
  CASH_RESPONSIBLE_REQUIRED: 'responsiblePerson',
  CASH_EXPENSE_ACCOUNT_REQUIRED: 'expenseAccountId',
  CASH_EXPENSE_ACCOUNT_INVALID: 'expenseAccountId',
}

const RESPONSIBLE_TEXT: Record<'DEPOSITED' | 'CARRIED' | 'NONE', { label: string; hint: string; required: string }> = {
  DEPOSITED: {
    label: 'cashdesk.transfer.depositedBy',
    hint: 'cashdesk.transfer.depositedByHint',
    required: 'cashdesk.transfer.depositedByRequired',
  },
  CARRIED: {
    label: 'cashdesk.transfer.responsible',
    hint: 'cashdesk.transfer.responsibleHint',
    required: 'cashdesk.transfer.responsibleRequired',
  },
  NONE: {
    label: 'cashdesk.transfer.responsibleAny',
    hint: 'cashdesk.transfer.responsibleAnyHint',
    required: 'cashdesk.transfer.responsibleAnyRequired',
  },
}

interface CashFormModalProps {
  mode: CashFormMode | null
  onClose: () => void
}

const TEXT_MAX = 120

export function CashFormModal({ mode, onClose }: CashFormModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const branches = useCashBranches()
  const [form] = Form.useForm<CashFormValues>()
  const [files, setFiles] = useState<UploadFile[]>([])
  const [failedFiles, setFailedFiles] = useState<string[]>([])
  const key = useRef('')
  const kind = Form.useWatch('kind', form)
  const transferType = Form.useWatch('transferType', form)
  const transferTypes = useTransferTypes(mode === 'transfer')
  const pickedBranch = Form.useWatch('branchId', form)
  const entryBranch = branches.sole?.id ?? pickedBranch ?? null
  const expenseAccounts = useQuery({
    queryKey: queryKeys.cashdesk.expenseAccounts(entryBranch ?? ''),
    queryFn: ({ signal }) => fetchExpenseAccounts(entryBranch ?? '', signal),
    enabled: mode === 'entry' && kind === 'EXPENSE' && entryBranch !== null,
    staleTime: 60_000,
  })
  const selectedType = transferTypes.data?.find((type) => type.code === transferType)
  const responsibleText = RESPONSIBLE_TEXT[selectedType ? (selectedType.referenceRequired ? 'DEPOSITED' : 'CARRIED') : 'NONE']
  const currency = user?.tenant.currency ?? getCurrencyCode()
  const needsBranchChoice = !branches.unknown && branches.active.length > 1

  useEffect(() => {
    const chosen = form.getFieldValue('expenseAccountId') as string | undefined
    if (chosen && expenseAccounts.data && !expenseAccounts.data.some((account) => account.id === chosen)) {
      form.setFieldValue('expenseAccountId', undefined)
    }
  }, [expenseAccounts.data, form])

  useEffect(() => {
    if (mode === null) return
    key.current = newIdempotencyKey(mode === 'entry' ? 'cash-entry' : 'cash-transfer')
    setFiles([])
    setFailedFiles([])
    form.resetFields()
  }, [mode, form])

  const save = useMutation({
    mutationFn: async (values: CashFormValues) => {
      const branchId = branches.sole?.id ?? values.branchId ?? null
      const amount = values.amount?.amount ?? 0
      const owner: AttachmentOwner = mode === 'entry' ? 'entries' : 'transfers'
      const created =
        mode === 'entry'
          ? await createEntry(
              {
                branchId,
                kind: values.kind ?? 'EXPENSE',
                amount,
                expenseAccountId: values.kind === 'EXPENSE' ? (values.expenseAccountId ?? null) : null,
                reason: values.reason?.trim() ?? '',
              },
              key.current,
            )
          : await createTransfer(
              {
                branchId,
                amount,
                transferType: values.transferType ?? null,
                externalReference: selectedType?.referenceRequired ? values.externalReference?.trim() || null : null,
                responsiblePerson: values.responsiblePerson?.trim() ?? '',
                remarks: values.remarks?.trim() || null,
              },
              key.current,
            )
      const failed: string[] = []
      for (const file of files) {
        if (!file.originFileObj) continue
        try {
          await uploadAttachment(owner, created.id, file.originFileObj)
        } catch {
          failed.push(file.name)
        }
      }
      return failed
    },
    onSuccess: (failed) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
      if (failed.length > 0) {
        setFailedFiles(failed)
        return
      }
      form.resetFields()
      onClose()
    },
    onError: (error) => {
      const field = FIELD_ERRORS[errorTag(error) ?? '']
      if (field) form.setFields([{ name: field, errors: [translateError(error, t)] }])
    },
  })

  const fieldError = save.isError && FIELD_ERRORS[errorTag(save.error) ?? ''] !== undefined

  const close = () => {
    form.resetFields()
    save.reset()
    onClose()
  }

  const done = save.isSuccess && failedFiles.length > 0

  return (
    <Modal
      open={mode !== null}
      title={mode === 'entry' ? t('cashdesk.entry.createTitle') : t('cashdesk.transfer.createTitle')}
      okText={mode === 'entry' ? t('cashdesk.entry.submit') : t('cashdesk.transfer.submit')}
      cancelText={done ? t('common.close') : t('common.cancel')}
      okButtonProps={{ disabled: done }}
      confirmLoading={save.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={mode === 'entry' ? t('cashdesk.entry.twoStepNote') : t('cashdesk.transfer.flowNote')}
        />
        {save.isError && !fieldError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
        {done ? (
          <Alert
            type="warning"
            showIcon
            message={t('cashdesk.attachment.partialUpload')}
            description={failedFiles.join(', ')}
          />
        ) : null}

        <Form<CashFormValues>
          form={form}
          layout="vertical"
          requiredMark
          preserve={false}
          disabled={done}
          initialValues={{ kind: 'EXPENSE', amount: null }}
        >
          {needsBranchChoice ? (
            <Form.Item
              name="branchId"
              label={t('cashdesk.branch')}
              rules={[{ required: true, message: t('cashdesk.branchRequired') }]}
            >
              <CashBranchSelect branches={branches.active} loading={branches.isPending} />
            </Form.Item>
          ) : null}

          {mode === 'transfer' ? (
            <Form.Item
              name="transferType"
              label={t('cashdesk.transfer.type')}
              rules={[{ required: true, message: t('cashdesk.transfer.typeRequired') }]}
            >
              <Select<string>
                placeholder={t('cashdesk.transfer.typePlaceholder')}
                loading={transferTypes.isPending}
                options={(transferTypes.data ?? [])
                  .filter((type) => type.active)
                  .map((type) => ({ value: type.code, label: transferTypeName(type.code, transferTypes.data, t) }))}
              />
            </Form.Item>
          ) : null}

          {mode === 'entry' ? (
            <Form.Item name="kind" label={t('cashdesk.entry.kind')}>
              <Select<EntryKind>
                options={ENTRY_KINDS.map((value) => ({ value, label: t(`cashdesk.entryKind.${value}`) }))}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="amount"
            label={t('cashdesk.payment.amount')}
            rules={[
              {
                validator: (_, value: Money | null | undefined) =>
                  value && value.amount > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('cashdesk.payment.amountRequired'))),
              },
            ]}
          >
            <MoneyInput currency={currency} minMinor={1} style={{ width: '100%' }} />
          </Form.Item>

          {mode === 'entry' && kind === 'EXPENSE' ? (
            <Form.Item
              name="expenseAccountId"
              label={t('cashdesk.entry.category')}
              rules={[{ required: true, message: t('cashdesk.entry.categoryRequired') }]}
              {...(expenseAccounts.isError ? { validateStatus: 'error', help: translateError(expenseAccounts.error, t) } : {})}
            >
              <Select<string>
                showSearch
                optionFilterProp="label"
                loading={entryBranch !== null && expenseAccounts.isPending}
                disabled={entryBranch === null}
                placeholder={t(entryBranch === null ? 'cashdesk.entry.categoryBranchFirst' : 'cashdesk.entry.categoryPick')}
                options={(expenseAccounts.data ?? []).map((account) => ({
                  value: account.id,
                  label: `${account.code} · ${seededName('accounts', account.code, account.name, t)}`,
                }))}
              />
            </Form.Item>
          ) : null}

          {mode === 'entry' ? (
            <Form.Item
              name="reason"
              label={t('actions.reason')}
              rules={[
                { required: true, whitespace: true, message: t('cashdesk.decision.reasonRequired') },
                { max: REASON_MAX, message: t('cashdesk.reasonTooLong') },
              ]}
            >
              <Input.TextArea rows={3} maxLength={REASON_MAX} showCount />
            </Form.Item>
          ) : (
            <>
              {selectedType?.referenceRequired ? (
                <Form.Item
                  name="externalReference"
                  label={referenceLabel(selectedType, t)}
                  extra={t('cashdesk.transfer.referenceUnique')}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t('cashdesk.transfer.referenceRequired', { label: referenceLabel(selectedType, t) }),
                    },
                    {
                      validator: (_, value: string | undefined) => {
                        const problem = value?.trim() ? referenceProblem(selectedType, value) : null
                        return problem
                          ? Promise.reject(
                              new Error(
                                t(`cashdesk.transfer.reference_${problem}`, {
                                  min: selectedType.referenceMinLength ?? 1,
                                  max: selectedType.referenceMaxLength ?? TRANSFER_REFERENCE_MAX,
                                }),
                              ),
                            )
                          : Promise.resolve()
                      },
                    },
                  ]}
                >
                  <Input maxLength={selectedType.referenceMaxLength ?? TRANSFER_REFERENCE_MAX} />
                </Form.Item>
              ) : null}
              <Form.Item
                name="responsiblePerson"
                label={t(responsibleText.label)}
                extra={t(responsibleText.hint)}
                rules={[{ required: true, whitespace: true, message: t(responsibleText.required) }]}
              >
                <Input maxLength={TEXT_MAX} />
              </Form.Item>
              <Form.Item name="remarks" label={t('cashdesk.transfer.remarks')}>
                <Input.TextArea rows={2} maxLength={REASON_MAX} showCount />
              </Form.Item>
            </>
          )}

          <Form.Item label={t('cashdesk.attachment.title')} extra={t('cashdesk.attachment.limits')}>
            <Upload
              accept={ATTACHMENT_ACCEPT}
              multiple
              fileList={files}
              beforeUpload={(file) => {
                if (file.size > ATTACHMENT_MAX_BYTES) {
                  void message.warning(t('cashdesk.attachment.tooLarge'))
                  return false
                }
                setFiles((current) => [
                  ...current,
                  { uid: file.uid, name: file.name, size: file.size, originFileObj: file },
                ])
                return false
              }}
              onRemove={(file) => setFiles((current) => current.filter((f) => f.uid !== file.uid))}
            >
              <Button icon={<UploadOutlined />}>{t('cashdesk.attachment.pick')}</Button>
            </Upload>
          </Form.Item>
          {mode === 'transfer' ? (
            <Typography.Text type="secondary">{t('cashdesk.transfer.fullAmountNote')}</Typography.Text>
          ) : null}
        </Form>
      </Space>
    </Modal>
  )
}
